import React from 'react';
import { ContextMenuState } from 'src/types/ViewModel/Vessel/TankPlan';
import { useStowageStore } from 'src/context/StowageContext';
import { hasPermission } from 'src/utils/stowage/permissions/stowagePermissionsUtils';
import { useAuthStore } from 'src/context/AuthContext';
import { stowageFeatureEnum } from 'src/enums/stowage/stowageFeatureEnum';
import { useRotationStore } from 'src/context/RotationContext';
import { getLastBerthCallInPortCall, isBefore } from 'src/utils/stowage/stowageRotationUtils';
import { autoStow } from 'src/utils/stowage/autoStowUtils';
import { useVesselStore } from 'src/context/VesselContext';
import useStowageApi from 'src/api/stowage/usePostStowageApi';
import { StowagePlanChange } from 'src/types/ViewModel/Stowage/StowagePlanChange';
import { createDeleteStowagePlanChange, createStowagePlanChange } from 'src/factory/Stowage/StowagePlanChanges/StowagePlanChangeFactory';
import { StowageChangeDataType } from 'src/types/ViewModel/Stowage/Enums/StowageChangeDataType';
import { getNewRandomColorId } from 'src/utils/stowage/cargoColorsUtils';
import OdfjellContextMenu, { MenuItem } from 'src/components/ui/odfjellContextMenu/OdfjellContextMenu';
import { getTankContentsInPortCall } from 'src/utils/stowage/manifest/cargoManifestUtils';
import { getAllTanks } from 'src/utils/stowage/tankPlanUtils';
import { deepCopyStowagePlan } from 'src/factory/Stowage/StowagePlanFactory';
import { deepCopy } from 'src/utils/objectUtils';

interface TankPlanContextMenuProps {
    contextMenuState: ContextMenuState;
}

const BottomTankPlanContextMenu: React.FC<TankPlanContextMenuProps> = ({ contextMenuState }) => {
    const { selectedVessel } = useVesselStore();
    const { activeStowagePlan, tankPlan } = useStowageStore();
    const { selectedStowageRotationPortCall, selectedStowageRotationBerthCall } = useRotationStore();
    const { saveStowageData } = useStowageApi();

    const { role } = useAuthStore();

    const onUndoStowage = () => {
        const berthCall = selectedStowageRotationBerthCall ?? getLastBerthCallInPortCall(selectedStowageRotationPortCall);
        if (!berthCall) {
            return;
        }
        const _stowagePlan = deepCopyStowagePlan(activeStowagePlan);
        const stowagePlanChanges: StowagePlanChange[] = [];
        _stowagePlan.stowageItems = [
            ..._stowagePlan.stowageItems.filter((si) => {
                if (
                    si.cargoOperation.berthCall.berthCallGuid !== berthCall.berthCallGuid &&
                    isBefore(_stowagePlan.berthCalls, si.cargoOperation.berthCall, berthCall)
                ) {
                    return si;
                } else {
                    stowagePlanChanges.push(
                        createDeleteStowagePlanChange(_stowagePlan.stowagePlanGuid, si.stowageGuid, StowageChangeDataType.StowageItem)
                    );
                }
            }),
        ];
        saveStowageData(_stowagePlan, stowagePlanChanges);
    };

    const onAutoStow = () => {
        const _stowagePlan = deepCopyStowagePlan(activeStowagePlan);
        const stowagePlanChanges = autoStow(selectedVessel, _stowagePlan, selectedStowageRotationPortCall, selectedStowageRotationBerthCall);
        saveStowageData(_stowagePlan, stowagePlanChanges);
    };

    const onRandomizeColors = () => {
        const tanks = getAllTanks(deepCopy(tankPlan));
        const visibleCargoes = Array.from(
            new Set(
                tanks
                    .flatMap((tank) => getTankContentsInPortCall(activeStowagePlan, selectedStowageRotationPortCall, tank))
                    .flatMap((tc) => tc.cargoContents)
                    .flatMap((cc) => cc.cargo)
            )
        );
        const stowagePlanChanges: StowagePlanChange[] = [];
        visibleCargoes.forEach((cargo) => {
            cargo.colorId = getNewRandomColorId();
            stowagePlanChanges.push(
                createStowagePlanChange(activeStowagePlan.stowagePlanGuid, cargo.cargoGuid, StowageChangeDataType.RotationCargo, {
                    colorId: cargo.colorId,
                })
            );
        });
        saveStowageData(activeStowagePlan, stowagePlanChanges);
    };

    const menuItems = [
        {
            content: 'Undo stowages from this call',
            isDisabled: !hasPermission(role, stowageFeatureEnum.EmptyTankPlan),
            onClick: () => onUndoStowage(),
        },
        {
            content: 'Auto-stow',
            isDisabled: !hasPermission(role, stowageFeatureEnum.AutoStow),
            onClick: () => onAutoStow(),
        },
        {
            content: 'New cargo colors',
            isDisabled: !hasPermission(role, stowageFeatureEnum.EditStowage),
            onClick: () => onRandomizeColors(),
        },
    ];

    const createMenuItems = (): MenuItem[] =>
        menuItems.map((item) => ({
            isSeparator: false,
            content: item.content,
            isDisabled: item.isDisabled,
            onClick: item.onClick,
            className: undefined,
        }));

    return (
        <OdfjellContextMenu
            position={{ x: contextMenuState.x, y: contextMenuState.y }}
            menuItems={createMenuItems()}
            dataContextMenu="tank-plan-bottom"
            className="tank-plan-bottom-context-menu"
        />
    );
};

export default BottomTankPlanContextMenu;
